// src/components/OnboardingTour.js
// 初回利用時のオンボーディングツアー（親切・丁寧版）

import React, { useState, useEffect, createContext, useContext } from 'react';
import { useTheme } from '../lib/ThemeContext';
import {
  IconDashboard, IconClients, IconProjects, IconExpenses,
  IconInvoices, IconAnalytics, IconAi, IconWorkProof,
  IconCheck, IconAdd, IconEdit, IconDownload,
} from '../lib/icons';

const ONBOARDING_KEY = 'totono_onboarding_done';

const STEPS = [
  // ── WELCOME ──────────────────────────────────────────────────
  {
    icon: IconDashboard,
    color: '#6366f1',
    badge: 'ようこそ',
    title: 'Totonoeeへようこそ！',
    desc: 'フリーランスのお金と仕事を、ひとつにまとめるアプリです。\n\n案件管理から請求書・経費まで、仕事に必要な情報をこのアプリ一つで管理できます。\n\n各機能を順番にご紹介します。（1分ほどで完了します）',
    tips: null,
    isWelcome: true,
  },

  // ── STEP 1: 最初にやること ────────────────────────────────────
  {
    icon: IconEdit,
    color: '#f59e0b',
    badge: 'STEP 1',
    title: 'まずはプロフィールを設定',
    desc: '左下（またはメニュー）の「プロフィール設定」から、以下を入力しておきましょう。',
    tips: [
      '✦ 表示名・事業者情報（署名欄）',
      '✦ 振込先情報（請求書に自動で記載）',
      '✦ インボイス登録番号（任意）',
      '✦ 電子印の文字（稼働証明書に押印）',
    ],
    hint: 'プロフィールを設定しておくと、請求書・稼働証明書に自動で情報が反映されます。',
  },

  // ── STEP 2: クライアント ──────────────────────────────────────
  {
    icon: IconClients,
    color: '#7dd3fc',
    badge: 'STEP 2',
    title: 'クライアントを登録する',
    desc: '取引先の会社情報を登録しておくと、案件や請求書で自動的に紐づきます。',
    tips: [
      '✦ 会社名・担当者名・連絡先を登録',
      '✦ 案件数・請求総額・入金率が一目でわかる',
      '✦ メールアドレスからワンタップで連絡',
      '✦ 右上「＋ 新規登録」から追加できます',
    ],
    hint: '先にクライアントを登録しておくと、案件作成がスムーズになります。',
  },

  // ── STEP 3: 案件管理 ──────────────────────────────────────────
  {
    icon: IconProjects,
    color: '#6366f1',
    badge: 'STEP 3',
    title: '案件を登録・管理する',
    desc: '進行中の仕事を登録して、ステータスで管理しましょう。',
    tips: [
      '✦ 商談中 → 受注 → 進行中 → 請求済み → 完了',
      '✦ 納品・時給・単発・月契約の4タイプに対応',
      '✦ 時給・単発はカレンダーで稼働日を記録',
      '✦ 案件カードから直接経費を追加できます',
    ],
    hint: '案件を登録すると、ダッシュボードの「未請求カード」に自動集計されます。',
  },

  // ── STEP 4: 経費管理 ──────────────────────────────────────────
  {
    icon: IconExpenses,
    color: '#f59e0b',
    badge: 'STEP 4',
    title: '経費を記録する',
    desc: '仕事でかかった費用を記録しておくと、確定申告や収支管理に役立ちます。',
    tips: [
      '✦ 交通費・機材・ソフトウェアなどカテゴリ別に管理',
      '✦ レシート画像を撮影して添付可能',
      '✦ 案件に紐づけて経費を管理',
      '✦ 収支分析に自動で反映されます',
    ],
    hint: '右上のカメラボタンでレシートをその場で撮影・記録できます。',
  },

  // ── STEP 5: 請求管理 ──────────────────────────────────────────
  {
    icon: IconInvoices,
    color: '#a78bfa',
    badge: 'STEP 5',
    title: '請求書を作成・管理する',
    desc: '案件完了後は、請求書を作成してPDFで出力・送付できます。',
    tips: [
      '✦ 案件と紐づけてワンクリックで請求書作成',
      '✦ PDFで出力してそのまま送付',
      '✦ AIが請求メールの文章を自動生成',
      '✦ 入金済みにするとダッシュボードに反映',
    ],
    hint: 'プロフィールの振込先・署名が請求書PDFに自動で記載されます。',
  },

  // ── STEP 6: 稼働証明書 ────────────────────────────────────────
  {
    icon: IconWorkProof,
    color: '#93c5fd',
    badge: 'STEP 6',
    title: '稼働証明書を出力する',
    desc: '複数案件の稼働日をまとめてPDF・Excelで出力できます。',
    tips: [
      '✦ 出力したい案件を選択するだけ',
      '✦ 月ごとにフィルタリング可能',
      '✦ 電子印（判子）の押印にも対応',
      '✦ PDFとExcel両形式で出力可能',
    ],
    hint: 'プロフィールで電子印の文字を設定しておくと、確認印欄に自動で押印されます。',
  },

  // ── STEP 7: 収支分析 ─────────────────────────────────────────
  {
    icon: IconAnalytics,
    color: '#34d399',
    badge: 'STEP 7',
    title: '収支を分析する',
    desc: '入金済みの請求書と経費データをもとに、収支状況を可視化します。',
    tips: [
      '✦ 月別の収入・経費をグラフで確認',
      '✦ 利益と納税概算（20%）を自動計算',
      '✦ 請求書・経費の一覧も確認できる',
      '✦ データが増えるほど精度が上がります',
    ],
    hint: '確定申告の時期に「納税概算」を参考にできます。あくまで概算ですのでご注意ください。',
  },

  // ── STEP 8: AIアドバイス ─────────────────────────────────────
  {
    icon: IconAi,
    color: '#c084fc',
    badge: 'STEP 8',
    title: 'AIに相談する',
    desc: 'あなたのデータをもとに、AIがビジネスのアドバイスをします。',
    tips: [
      '✦ 「今月の収支を分析して」と聞いてみよう',
      '✦ 単価アップ・節税・営業の相談も可能',
      '✦ 確定申告の準備リストを出してもらえる',
      '✦ 営業メールの文章作成も得意です',
    ],
    hint: 'データが蓄積されるほど、より精度の高いアドバイスが得られます。',
  },

  // ── COMPLETE ──────────────────────────────────────────────────
  {
    icon: IconCheck,
    color: '#34d399',
    badge: '完了',
    title: '準備完了です！',
    desc: 'ツアーは以上です。\n\nまずはプロフィール設定とクライアント登録から始めてみましょう。',
    tips: [
      '✦ プロフィール設定（左下のメニューから）',
      '✦ クライアントを登録する',
      '✦ 最初の案件を追加する',
    ],
    hint: 'このツアーはプロフィール画面の「機能ツアーをもう一度見る」からいつでも再表示できます。',
    isComplete: true,
  },
];

// ── ツアー状態を全アプリで1つに統一するためのContext ──
const OnboardingContext = createContext(null);

// App.js で認証済みアプリ全体を1回だけ包む。
// show判定・localStorage管理・ツアー本体の描画をすべてここに集約する。
export function OnboardingProvider({ children }) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem(ONBOARDING_KEY)) {
      const t = setTimeout(() => setShow(true), 900);
      return () => clearTimeout(t);
    }
  }, []);

  const finish = () => {
    localStorage.setItem(ONBOARDING_KEY, '1');
    setShow(false);
  };

  const reset = () => {
    localStorage.removeItem(ONBOARDING_KEY);
    setShow(true);
  };

  return (
    <OnboardingContext.Provider value={{ show, finish, reset }}>
      {children}
      {/* ツアー本体はここで1回だけ描画（position:fixedで全画面に重なる） */}
      {show && <OnboardingTour onFinish={finish} />}
    </OnboardingContext.Provider>
  );
}

// 各画面はこのフックで共有stateを参照する（Profile.jsのreset等）。
// Provider外で呼ばれても落ちないようフォールバックを返す。
export function useOnboarding() {
  const ctx = useContext(OnboardingContext);
  if (!ctx) return { show: false, finish: () => {}, reset: () => {} };
  return ctx;
}

export default function OnboardingTour({ onFinish }) {
  const { theme } = useTheme();
  const [step, setStep] = useState(0);
  const [exiting, setExiting] = useState(false);
  const [animDir, setAnimDir] = useState(1); // 1=次 -1=前

  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;
  const Icon = current.icon;
  const progress = Math.round((step / (STEPS.length - 1)) * 100);

  const goTo = (nextStep) => {
    setAnimDir(nextStep > step ? 1 : -1);
    setStep(nextStep);
  };

  const next = () => {
    if (isLast) {
      handleFinish();
    } else {
      goTo(step + 1);
    }
  };

  const prev = () => {
    if (step > 0) goTo(step - 1);
  };

  const handleFinish = () => {
    setExiting(true);
    setTimeout(() => onFinish(), 280);
  };

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) handleFinish(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px',
        opacity: exiting ? 0 : 1,
        transition: 'opacity 0.28s',
      }}
    >
      <div style={{
        background: theme.bgCard,
        border: `1px solid ${theme.border}`,
        borderRadius: 24,
        width: '100%',
        maxWidth: 420,
        maxHeight: '90vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 32px 80px rgba(0,0,0,0.5)',
        transform: exiting ? 'translateY(12px) scale(0.97)' : 'translateY(0) scale(1)',
        transition: 'transform 0.28s',
      }}>

        {/* プログレスバー */}
        <div style={{ height: 3, background: theme.border, flexShrink: 0 }}>
          <div style={{
            height: '100%',
            width: `${progress}%`,
            background: `linear-gradient(90deg, ${current.color}, ${theme.accentSub})`,
            transition: 'width 0.4s ease',
          }}/>
        </div>

        {/* スクロール可能なコンテンツ */}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          <div style={{ padding: '24px 24px 20px' }}>

            {/* バッジ + アイコン */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
              <div style={{
                width: 52, height: 52, borderRadius: 16,
                background: current.color + '20',
                border: `1.5px solid ${current.color}50`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <Icon size={26} color={current.color} />
              </div>
              <div>
                <div style={{
                  display: 'inline-block',
                  fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
                  color: current.color,
                  background: current.color + '18',
                  border: `1px solid ${current.color}40`,
                  borderRadius: 20, padding: '2px 10px',
                  marginBottom: 4,
                }}>
                  {current.badge}
                </div>
                <div style={{ fontSize: 18, fontWeight: 700, color: theme.textPrimary, lineHeight: 1.3 }}>
                  {current.title}
                </div>
              </div>
            </div>

            {/* 説明文 */}
            <div style={{
              fontSize: 14, color: theme.textSecondary,
              lineHeight: 1.8, whiteSpace: 'pre-line',
              marginBottom: current.tips ? 16 : 0,
            }}>
              {current.desc}
            </div>

            {/* Tipsリスト */}
            {current.tips && (
              <div style={{
                background: theme.bgInput,
                border: `1px solid ${theme.border}`,
                borderRadius: 12,
                padding: '12px 14px',
                marginBottom: 14,
                display: 'flex', flexDirection: 'column', gap: 7,
              }}>
                {current.tips.map((tip, i) => (
                  <div key={i} style={{ fontSize: 13, color: theme.textPrimary, lineHeight: 1.5 }}>
                    {tip}
                  </div>
                ))}
              </div>
            )}

            {/* ヒント */}
            {current.hint && (
              <div style={{
                display: 'flex', gap: 8, alignItems: 'flex-start',
                background: current.color + '12',
                border: `1px solid ${current.color}30`,
                borderRadius: 10, padding: '10px 12px',
                marginBottom: 4,
              }}>
                <div style={{ fontSize: 13, flexShrink: 0, marginTop: 1 }}>💡</div>
                <div style={{ fontSize: 12, color: theme.textSecondary, lineHeight: 1.6 }}>
                  {current.hint}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* フッター固定 */}
        <div style={{
          padding: '14px 24px 20px',
          borderTop: `1px solid ${theme.border}`,
          flexShrink: 0,
          background: theme.bgCard,
        }}>
          {/* ステップドット */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 5, marginBottom: 14 }}>
            {STEPS.map((_, i) => (
              <div key={i} onClick={() => goTo(i)} style={{
                width: i === step ? 20 : 6,
                height: 6, borderRadius: 3, cursor: 'pointer',
                background: i === step ? current.color : theme.border,
                transition: 'all 0.25s',
              }}/>
            ))}
          </div>

          {/* ボタン */}
          <div style={{ display: 'flex', gap: 8 }}>
            {/* 戻るボタン（STEP2以降に表示） */}
            {step > 0 && !current.isWelcome && (
              <button onClick={prev} style={{
                width: 42, height: 44, borderRadius: 10,
                border: `1px solid ${theme.border}`,
                background: 'transparent',
                color: theme.textMuted, fontSize: 16,
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                ←
              </button>
            )}

            {/* スキップ（WELCOMEとCOMPLETE以外） */}
            {!current.isWelcome && !current.isComplete && (
              <button onClick={handleFinish} style={{
                flex: 1, padding: '11px', borderRadius: 10,
                border: `1px solid ${theme.border}`,
                background: 'transparent',
                fontSize: 13, color: theme.textMuted, cursor: 'pointer',
              }}>
                スキップ
              </button>
            )}

            {/* メインボタン */}
            <button onClick={next} style={{
              flex: current.isWelcome || current.isComplete ? 1 : 2,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              padding: '12px 16px', borderRadius: 10, border: 'none',
              background: current.color,
              color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer',
              transition: 'opacity 0.15s',
            }}>
              {current.isComplete ? (
                <><IconCheck size={15} color="#fff" /> さっそく始める</>
              ) : current.isWelcome ? (
                'ツアーを始める →'
              ) : (
                `次へ (${step} / ${STEPS.length - 1})`
              )}
            </button>
          </div>

          {/* ステップ番号テキスト */}
          {!current.isWelcome && !current.isComplete && (
            <div style={{ textAlign: 'center', marginTop: 8, fontSize: 11, color: theme.textWeak }}>
              {step} / {STEPS.length - 1} ステップ完了
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
